import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiAlertTriangle, FiCheck, FiCopy, FiRefreshCw, FiUpload } from "react-icons/fi";

import { ReactComponent as LineChartIcon } from "../../assets/icons/line-chart.svg";
import { ReactComponent as ActivityIcon } from "../../assets/icons/activity.svg";
import { ReactComponent as LayersTwoIcon } from "../../assets/icons/layers-two-02.svg";
import { Skeleton, SkeletonText } from "../Skeleton";
import { imageCache } from "../../utils/imageCache";
import { describeDatasetColumns, formatDatasetType } from "../../utils/datasetUtils";
import { describeUnmapped, observedColumns } from "../../utils/datasetColumnMapping";
import { pickSubject } from "../../utils/modelsList";

// Keyed to the dataset's type, not hashed off its id: the three types are what a grid must tell apart
const TYPE_STYLES: Record<string, { band: string; Icon: any }> = {
  HISTORICAL: { band: "bg-eai-teal-deep", Icon: LineChartIcon },
  FORECAST: { band: "bg-eai-cta-primary", Icon: ActivityIcon },
  ENSEMBLE_FORECAST: { band: "bg-eai-slash", Icon: LayersTwoIcon }
};

const FALLBACK_STYLE = { band: "bg-eai-surface-dark", Icon: LineChartIcon };

interface DatasetCardProps {
  dataset: any;
  canManage: boolean;
  /** Upload/append controls; defaults to canManage. Org members may upload to an org dataset they can't edit */
  canUpload?: boolean;
  onOpen: (dataset: any) => void;
  onEdit: (dataset: any) => void;
  /** The "not mapped" flag was clicked: open the column mapping */
  onMapColumns?: (dataset: any) => void;
  onUpload: (dataset: any) => void;
  onRefreshCount: (dataset: any) => void;
  isRefreshing?: boolean;
  /** The historical model whose runs write this dataset (Dataset.sourceModelId), for the "Written by" pill */
  sourceModelName?: string | null;
}

// The group tiles' rail card — the table this replaced put a raw UUID under every name
const DatasetCard: React.FC<DatasetCardProps> = ({
  dataset,
  canManage,
  canUpload = canManage,
  onOpen,
  onEdit,
  onMapColumns,
  onUpload,
  onRefreshCount,
  isRefreshing,
  sourceModelName
}) => {
  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { band, Icon } = TYPE_STYLES[dataset?.datasetType] || FALLBACK_STYLE;
  const isEmpty = !dataset?.recordCount;
  // Columns the dataset holds that nobody has described; the flag opens the mapping
  const unmappedNote = describeUnmapped(dataset);
  // The glyph and band name what the data is about (wind, price, flow…); the type's own glyph and band stand in when nothing matches
  const subject = useMemo(
    () =>
      pickSubject(
        dataset?.name,
        (dataset?.tags || []).join(" "),
        dataset?.parameterType,
        dataset?.description,
        observedColumns({ columns: dataset?.columns }).join(" ")
      ),
    [dataset?.name, dataset?.tags, dataset?.parameterType, dataset?.description, dataset?.columns]
  );
  const SubjectIcon = subject?.Icon;

  useEffect(() => {
    let cancelled = false;
    setBackgroundUrl(null);
    if (!dataset?.profilePictureUrl) return;
    imageCache
      .getSignedUrl(dataset.profilePictureUrl)
      .then((url) => {
        if (!cancelled) setBackgroundUrl(url || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [dataset?.profilePictureUrl]);

  // A card can unmount (filter, delete, navigation) inside the "Copied" window
  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    []
  );

  // The id is what the REST API addresses a dataset by, so it has to be liftable off the card
  const copyId = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(dataset.id);
      setCopiedId(true);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopiedId(false), 1500);
    } catch (error) {
      console.error("Could not copy dataset id:", error);
    }
  };

  // Every card opens the detail view. An empty one opens on its upload log, so a first upload that failed can be
  // read; the "Upload data" button on an empty card still goes straight to the upload modal
  const activate = () => onOpen(dataset);

  return (
    // The whole card opens the data; inner controls stop propagation so managing never also opens
    <div
      className="eai-rail-card group w-full min-w-0 cursor-pointer"
      role="button"
      tabIndex={0}
      aria-label={isEmpty ? `View uploads for ${dataset.name}` : `View ${dataset.name}`}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activate();
        }
      }}
    >
      <div
        className={`eai-rail ${backgroundUrl ? "bg-eai-surface bg-cover bg-center" : subject?.band ?? band}`}
        style={backgroundUrl ? { backgroundImage: `url(${backgroundUrl})` } : undefined}
      >
        {/* Without a picture the rail is a type band — the glyph is what makes it read as data */}
        {!backgroundUrl &&
          (SubjectIcon ? (
            <SubjectIcon aria-hidden className="h-9 w-9 text-white" />
          ) : (
            <Icon aria-hidden className="h-9 w-9 text-white [&_path]:stroke-current" />
          ))}
      </div>

      {/* Shared gap; only the padding is tighter, like ModelSchemaCard: a fact block and an action row still have to land on the 208px floor */}
      <div className="eai-rail-copy gap-3 py-3">
        {/* Every block sits one gap-3 apart; no reserved heights, so the spacing is the same top to bottom */}
        <h3 className="eai-card-title line-clamp-2 break-words" title={dataset.name}>
          {dataset.name}
        </h3>
        {dataset.description && (
          <p className="eai-card-description min-h-0" title={dataset.description}>
            {dataset.description}
          </p>
        )}

        {/* Type and size as the same pills TemplateCard uses; they wrap rather than clip on a narrow card */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="eai-tag-pill-sm whitespace-nowrap">{formatDatasetType(dataset.datasetType)}</span>
          <span className="eai-tag-pill-sm whitespace-nowrap">{describeDatasetColumns(dataset)}</span>
          {sourceModelName && (
            <span className="eai-tag-pill-sm max-w-full truncate" title={`A historical model's runs fill this dataset: ${sourceModelName}`} data-testid="written-by-model">
              Written by {sourceModelName}
            </span>
          )}
          {/* Unlabelled columns: a warning at the end of the tag row, so it lines up with them */}
          {unmappedNote && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMapColumns?.(dataset);
              }}
              aria-label={`${unmappedNote} for ${dataset.name}`}
              title={`${unmappedNote}. Click to label them.`}
              data-testid="dataset-card-unmapped"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full text-eai-warning-text hover:bg-eai-brand-peach focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eai-focus-neutral active:bg-eai-brand-peach"
            >
              <FiAlertTriangle size={14} aria-hidden />
            </button>
          )}
        </div>

        {/* Clicking the card opens the data; mt-auto pins the controls to the bottom of every card */}
        <div className="mt-auto flex items-center gap-2">
          {/* An empty dataset's first move is an upload, so it leads the row on the left, the way Run once does on a model */}
          {isEmpty && canUpload && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onUpload(dataset);
              }}
              className="eai-btn-cta-xs"
            >
              Upload data
            </button>
          )}
          {/* Copy id leads the icon group; delete lives in the edit modal's footer, beside Cancel and Save */}
          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={copyId}
              aria-label={`Copy dataset id for ${dataset.name}`}
              title={copiedId ? "Copied" : `Copy dataset id (${dataset.id})`}
              data-testid="dataset-card-copy-id"
              className="eai-btn-icon"
            >
              {copiedId ? <FiCheck size={12} /> : <FiCopy size={12} />}
            </button>
            {canManage && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRefreshCount(dataset);
                }}
                disabled={isRefreshing}
                aria-label={`Recheck record count for ${dataset.name}`}
                title="Recheck record count"
                data-testid="dataset-card-recheck"
                className="eai-btn-icon"
              >
                <FiRefreshCw size={12} className={isRefreshing ? "animate-spin" : ""} />
              </button>
            )}
            {canUpload && !isEmpty && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onUpload(dataset);
                }}
                aria-label={`Append data to ${dataset.name}`}
                title="Append data"
                className="eai-btn-icon"
              >
                <FiUpload size={12} />
              </button>
            )}
            {canManage && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(dataset);
                }}
                className="eai-btn-outline-xs"
              >
                Edit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder matching DatasetCard's layout — keep the two in sync when the card changes.
export const DatasetCardSkeleton = () => (
  <div className="eai-rail-card h-full w-full min-w-0">
    <Skeleton className="eai-rail" radius="none" />
    <div className="eai-rail-copy gap-3 py-3">
      <Skeleton className="h-3.5 w-2/3" />
      <SkeletonText lines={2} lineClassName="h-3" />
      <div className="flex gap-1.5">
        <Skeleton className="h-[23px] w-16" radius="full" />
        <Skeleton className="h-[23px] w-16" radius="full" />
      </div>
      <div className="mt-auto flex items-center">
        <div className="ml-auto flex items-center gap-1.5">
          <Skeleton className="h-eai-control-icon w-eai-control-icon" radius="full" />
          <Skeleton className="h-eai-control-icon w-eai-control-icon" radius="full" />
          <Skeleton className="h-eai-control-icon w-eai-control-icon" radius="full" />
          <Skeleton className="h-eai-control-xs w-12" radius="full" />
        </div>
      </div>
    </div>
  </div>
);

export default DatasetCard;
